import type {Token} from '../../../types'
import {isListType} from '../../utils'

/** Creates a listItem token with the given start and end positions. */
function createListItemToken(direction: 'enter' | 'exit', start: number, end: number): Token {
  return {
    direction,
    type: 'listItem',
    start,
    end,
  }
}

/** Open the list item in the stack by creating an enter token. Adds the enter token from the stack. */
function openListItem(result: Token[], listItemStack: Token[], startPosition: number, endPosition: number): void {
  const listItemEnterToken = createListItemToken('enter', startPosition, endPosition)
  result.push(listItemEnterToken)
  listItemStack.push(listItemEnterToken)
}

/**
 * Closes the last list item in the stack by creating an exit token and updating the enter token's end position.
 * Removes the enter token from the stack.
 */
function closeListItem(result: Token[], listItemStack: Token[], endPosition: number): void {
  const listItemEnterToken = listItemStack.pop()!
  listItemEnterToken.end = endPosition
  const listItemExitToken = createListItemToken('exit', listItemEnterToken.start, endPosition)
  result.push(listItemExitToken)
}

/** Handles list enter/exit tokens and manages the list stack. Closes the last open list item at the end of a list. */
function handleListToken(token: Token, result: Token[], listStack: Token[], listItemStack: Token[]): void {
  if (token.direction === 'enter') {
    listStack.push(token)
  } else {
    // List exit
    const listDepth = listStack.length
    if (listItemStack.length >= listDepth) {
      const consumedToken = result.pop()!
      closeListItem(result, listItemStack, token.end)
      result.push(consumedToken)
    }
    listStack.pop()
  }
}

/** Handles list item prefix and indent tokens. */
function handleListItemEnterToken(token: Token, result: Token[], listItemStack: Token[], listDepth: number): void {
  if (token.direction !== 'enter') return

  // TODO: Add support for multiple nested lists
  if (token.type === 'listItemIndent' && listDepth > 1) {
    openListItem(result, listItemStack, token.start, token.end)
  } else if (token.type === 'listItemPrefix' && listDepth === 1) {
    const consumedToken = result.pop()!
    openListItem(result, listItemStack, token.start, token.end)
    result.push(consumedToken)
  }
}

/**
 * Handles line ending tokens and closes list items when appropriate.
 * Currently supports flat lists and single-layered nested lists.
 * TODO: Add support for multi-layered nested lists and multi-paragraph list items where the line endings are between content tokens
 */
function handleLineEnding(
  token: Token,
  index: number,
  tokens: Token[],
  result: Token[],
  listItemStack: Token[],
  withinContentToken: boolean,
) {
  if (listItemStack.length === 0 || token.direction === 'enter' || withinContentToken) return

  // Skip processing line ending if the next token starts a list (handles nested lists)
  const nextToken = tokens[index + 1]
  if (nextToken && isListType(nextToken.type)) return

  closeListItem(result, listItemStack, token.end)
}

/** Wrap each list item in our custom `listItem` token for rendering. */
export function createListItemTokens(shiftedTokens: Token[]): Token[] {
  const result: Token[] = []
  const listStack: Token[] = []
  const listItemStack: Token[] = []
  let withinContentToken = false

  for (let index = 0; index < shiftedTokens.length; index++) {
    const token = shiftedTokens[index]!
    result.push(token)

    // Track content token boundaries
    if (token.type === 'content') {
      withinContentToken = token.direction === 'enter'
    }

    if (isListType(token.type)) {
      handleListToken(token, result, listStack, listItemStack)
    } else if (token.type === 'listItemIndent' || token.type === 'listItemPrefix') {
      const listDepth = listStack.length
      handleListItemEnterToken(token, result, listItemStack, listDepth)
    } else if (token.type === 'lineEnding') {
      handleLineEnding(token, index, shiftedTokens, result, listItemStack, withinContentToken)
    }
  }

  return result
}
